import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";

// Extrai o token de verificacao da URL (token_hash ou code como fallback)
function extractTokenFromUrl(): string | null {
  const hash = window.location.hash;
  const search = window.location.search;
  const hashParams = new URLSearchParams(hash.replace(/^#/, ""));
  const searchParams = new URLSearchParams(search);
  // Prioridade: token_hash (novo template direto), depois code (fallback/legado)
  return (
    searchParams.get("token_hash") ||
    hashParams.get("token_hash") ||
    searchParams.get("code") ||
    hashParams.get("code") ||
    null
  );
}

// Mapeia os codigos de erro do Supabase no hash para mensagens amigaveis
function parseHashError(hash: string): string | null {
  if (!hash) return null;

  const params = new URLSearchParams(hash.replace(/^#/, ""));
  const errorCode = params.get("error_code");
  const errorDesc = params.get("error_description");

  if (!errorCode && !params.get("error")) return null;

  switch (errorCode) {
    case "otp_expired":
      return "O link de redefinição expirou. Os links são válidos por 1 hora.";
    case "otp_no_authorization":
      return "Esse link de redefinição já foi usado.";
    default:
      return errorDesc
        ? decodeURIComponent(errorDesc).replace(/\+/g, " ")
        : "O link de redefinição é inválido ou já expirou.";
  }
}

// Traduz erros do verifyOtp para mensagens amigaveis
function parseVerifyOtpError(err: { message?: string; code?: string }): string {
  const msg = (err.message || "").toLowerCase();
  if (msg.includes("expired") || msg.includes("otp_expired")) {
    return "O link de redefinição expirou. Os links são válidos por 1 hora.";
  }
  if (msg.includes("already") || msg.includes("used") || msg.includes("consumed")) {
    return "Esse link de redefinição já foi usado.";
  }
  if (msg.includes("not found") || msg.includes("invalid")) {
    return "O link de redefinição é inválido ou já expirou.";
  }
  return "O link de redefinição é inválido ou já expirou.";
}

export default function ResetPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const [resetDone, setResetDone] = useState(false);

  // Detecta se chegou via link de reset com PKCE (code na URL)
  const [isRecoveryFlow, setIsRecoveryFlow] = useState(false);
  // Loading enquanto faz a troca PKCE do code pela sessao
  const [exchangingCode, setExchangingCode] = useState(false);
  // Erro vindo do hash da URL ou do verifyOtp (link expirado / ja usado)
  const [linkExpiredError, setLinkExpiredError] = useState<string | null>(null);
  // Usamos ref pra garantir que o cleanup tenha o valor mais recente
  const resetDoneRef = useRef(false);

  useEffect(() => {
    const hash = window.location.hash;

    // Verifica erros no hash primeiro (link expirado, ja usado, etc)
    if (hash) {
      const hashError = parseHashError(hash);
      if (hashError) {
        setLinkExpiredError(hashError);
        setIsRecoveryFlow(false);
        return;
      }
    }

    // PKCE: tenta extrair o token (token_hash ou code) da URL e trocar pela sessao
    const code = extractTokenFromUrl();
    if (code) {
      setExchangingCode(true);

      supabase.auth
        .verifyOtp({ token_hash: code, type: "recovery" })
        .then(({ error: pkceError }) => {
          if (pkceError) {
            setLinkExpiredError(parseVerifyOtpError(pkceError));
            setIsRecoveryFlow(false);
          } else {
            // Limpa a URL pra evitar re-troca em refresh
            window.history.replaceState(null, "", window.location.pathname);
            setIsRecoveryFlow(true);
          }
        })
        .finally(() => {
          setExchangingCode(false);
        });

      return;
    }

    // Se nao tem code e nem erro, fica no estado inicial (formulario de email)
  }, []);

  // Quando a pessoa sai da pagina sem completar o reset, faz logout
  // Isso impede que ela acesse o perfil pelo botao "voltar" do navegador
  useEffect(() => {
    return () => {
      if (isRecoveryFlow && !resetDoneRef.current) {
        supabase.auth.signOut();
      }
    };
  }, [isRecoveryFlow]);

  const handleGoBack = async () => {
    // No fluxo de recovery, faz logout antes de voltar pro login
    if (isRecoveryFlow && !resetDoneRef.current) {
      await supabase.auth.signOut();
    }
    navigate("/meu-perfil");
  };

  const handleSendResetEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    setError("");
    setLinkExpiredError(null);

    const basePath = __BASE_PATH__.split("/").filter(Boolean).join("/");
    const pathPrefix = basePath ? `/${basePath}` : "";

    const { error: authError } = await supabase.auth.resetPasswordForEmail(
      email.trim(),
      {
        redirectTo: `${window.location.origin}${pathPrefix}/recuperar-senha`,
      }
    );

    if (authError) {
      setError(
        authError.message === "User not found"
          ? "Não encontramos nenhuma conta com esse email."
          : authError.message || "Erro ao enviar o email. Tente novamente."
      );
      setLoading(false);
      return;
    }

    setSent(true);
    setLoading(false);
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim() || !confirmPassword.trim()) return;

    if (password !== confirmPassword) {
      setError("As senhas não coincidem.");
      return;
    }

    if (password.length < 6) {
      setError("A senha deve ter pelo menos 6 caracteres.");
      return;
    }

    setLoading(true);
    setError("");

    const { error: updateError } = await supabase.auth.updateUser({
      password: password.trim(),
    });

    if (updateError) {
      setError(updateError.message || "Erro ao redefinir a senha. Tente novamente.");
      setLoading(false);
      return;
    }

    // Marca como concluido tanto no state quanto na ref
    // A ref garante que o cleanup nao vai fazer logout indevido
    resetDoneRef.current = true;
    setResetDone(true);
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-beige flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-10">
          <Link
            to="/"
            className="font-display text-2xl md:text-3xl tracking-wide text-brown"
          >
            Entre Elas
          </Link>
          <div className="flex items-center justify-center gap-2 mt-3">
            <span className="inline-block w-6 h-px bg-primary/40" />
            <span className="inline-block w-1 h-1 rounded-full bg-primary" />
            <span className="inline-block w-6 h-px bg-primary/40" />
          </div>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl p-8 md:p-10 shadow-sm">
          {resetDone ? (
            /* Senha redefinida com sucesso */
            <div className="text-center py-4">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-50 mx-auto mb-6">
                <i className="ri-check-line text-2xl text-green-500" />
              </div>
              <h2 className="font-display text-xl md:text-2xl text-brown mb-3">
                Senha redefinida!
              </h2>
              <p className="font-body text-sm text-brown/70 leading-relaxed mb-6">
                Sua nova senha foi definida com sucesso. Agora você pode acessar seu perfil.
              </p>
              <button
                onClick={() => navigate("/meu-perfil")}
                className="w-full font-body text-sm px-6 py-3.5 rounded-full bg-primary text-white tracking-wide uppercase transition-all duration-300 hover:bg-primary-dark whitespace-nowrap cursor-pointer"
              >
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-login-box-line" />
                  Ir para o login
                </span>
              </button>
            </div>
          ) : exchangingCode ? (
            /* Trocando o code PKCE pela sessao... */
            <div className="text-center py-8">
              <div className="w-10 h-10 border-2 border-primary/20 border-t-primary rounded-full animate-spin mx-auto mb-4" />
              <p className="font-body text-sm text-brown/60">
                Verificando seu link...
              </p>
            </div>
          ) : linkExpiredError ? (
            /* Tela dedicada: link expirado ou ja usado */
            <div className="text-center py-4">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-amber-50 mx-auto mb-6">
                <i className="ri-timer-line text-2xl text-amber-500" />
              </div>
              <h2 className="font-display text-xl md:text-2xl text-brown mb-3">
                Link expirado
              </h2>
              <p className="font-body text-sm text-brown/70 leading-relaxed mb-6">
                {linkExpiredError}
              </p>
              <button
                onClick={() => setLinkExpiredError(null)}
                className="w-full font-body text-sm px-6 py-3.5 rounded-full bg-primary text-white tracking-wide uppercase transition-all duration-300 hover:bg-primary-dark whitespace-nowrap cursor-pointer"
              >
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-mail-send-line" />
                  Solicitar novo link
                </span>
              </button>
            </div>
          ) : isRecoveryFlow ? (
            /* Formulario de nova senha (PKCE trocado com sucesso) */
            <>
              <div className="text-center mb-6">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary/10 mx-auto mb-4">
                  <i className="ri-lock-password-line text-2xl text-primary" />
                </div>
                <h2 className="font-display text-xl md:text-2xl text-brown mb-2">
                  Redefinir senha
                </h2>
                <p className="font-body text-sm text-brown/60">
                  Crie uma nova senha para sua conta.
                </p>
              </div>

              {error && (
                <div className="mb-5 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 font-body text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                  <label className="font-body text-sm text-brown block mb-2">
                    Nova senha
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setError("");
                      }}
                      placeholder="Mínimo 6 caracteres"
                      required
                      className="w-full px-4 py-3 pr-10 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-brown/40 hover:text-brown/70 transition-colors cursor-pointer"
                      tabIndex={-1}
                    >
                      <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"} />
                    </button>
                  </div>
                </div>

                <div>
                  <label className="font-body text-sm text-brown block mb-2">
                    Confirmar nova senha
                  </label>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      setError("");
                    }}
                    placeholder="Repita a senha"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full font-body text-sm px-6 py-3.5 rounded-full bg-primary text-white tracking-wide uppercase transition-all duration-300 hover:bg-primary-dark disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin" />
                      Redefinindo...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-check-line" />
                      Redefinir senha
                    </span>
                  )}
                </button>
              </form>
            </>
          ) : sent ? (
            /* Email de reset enviado */
            <div className="text-center py-4">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-50 mx-auto mb-6">
                <i className="ri-mail-send-line text-2xl text-green-500" />
              </div>
              <h2 className="font-display text-xl md:text-2xl text-brown mb-3">
                Verifique seu email
              </h2>
              <p className="font-body text-sm text-brown/70 leading-relaxed mb-2">
                Enviamos um link de redefinição para <strong>{email}</strong>.
              </p>
              <p className="font-body text-xs text-brown/50 leading-relaxed mb-6">
                Clique no link que chegou no seu email para criar uma nova senha. Se não encontrar, verifique a pasta de spam.
              </p>
              <button
                onClick={() => {
                  setSent(false);
                  setError("");
                }}
                className="font-body text-sm text-primary hover:text-primary-dark transition-colors underline cursor-pointer"
              >
                Tentar com outro email
              </button>
            </div>
          ) : (
            /* Formulario para digitar o email (inicial ou apos erro no link) */
            <>
              <div className="text-center mb-6">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary/10 mx-auto mb-4">
                  <i className="ri-key-2-line text-2xl text-primary" />
                </div>
                <h2 className="font-display text-xl md:text-2xl text-brown mb-2">
                  Esqueci minha senha
                </h2>
                <p className="font-body text-sm text-brown/60">
                  Digite seu email de cadastro e enviaremos um link para você criar uma nova senha.
                </p>
              </div>

              {/* Erro generico (ex: email nao encontrado) */}
              {error && (
                <div className="mb-5 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 font-body text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleSendResetEmail} className="space-y-4">
                <div>
                  <label className="font-body text-sm text-brown block mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setError("");
                    }}
                    placeholder="seu@email.com"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full font-body text-sm px-6 py-3.5 rounded-full bg-primary text-white tracking-wide uppercase transition-all duration-300 hover:bg-primary-dark disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin" />
                      Enviando...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-mail-send-line" />
                      Enviar link de redefinição
                    </span>
                  )}
                </button>
              </form>
            </>
          )}

          {/* Link de voltar — aparece em todos os estados exceto sucesso final */}
          {!resetDone && (
            <div className="mt-6 pt-5 border-t border-nude text-center">
              <button
                onClick={handleGoBack}
                className="font-body text-sm text-brown/60 hover:text-brown transition-colors cursor-pointer"
              >
                <span className="flex items-center justify-center gap-1">
                  <i className="ri-arrow-left-line" />
                  Voltar para o login
                </span>
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-center font-body text-xs text-brown/40 mt-8">
          &copy; {new Date().getFullYear()} Entre Elas
        </p>
      </div>
    </div>
  );
}